/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { ComponentChildren } from 'preact';
import type { ListItemRendererContext } from '#UNSAFE_Collection';

import { classNames } from '#utils/UNSAFE_classNames';
import { getListCellClassArray } from './utils/ListThemeUtils';

/**
 * Props for the ListCell Component
 */
type ListCellProps<K, D> = {
  listId: string;
  rowData: D;
  rowKey: K;
  rowIndex: number;
  hasTabIndex: boolean;
  isSelectionEnabled: boolean;
  isSelected: boolean;
  isTabbableMode: boolean;
  isHighContrast: boolean;
  renderer?: (context: ListItemRendererContext<K, D>) => ComponentChildren;
  selectorRenderer?: () => ComponentChildren;
};

function _defaultCellRenderer() {
  return undefined;
}

/**
 * The internal component used to render a single cell in a ListRow.
 */
export function ListCell<K, D>({
  listId,
  rowData,
  rowKey,
  rowIndex,
  hasTabIndex,
  isSelectionEnabled,
  isSelected,
  isTabbableMode,
  renderer = _defaultCellRenderer,
  selectorRenderer,
  isHighContrast
}: ListCellProps<K, D>) {
  const cellClasses = classNames(getListCellClassArray({ isHighContrast }));
  const cellRendererProps = {
    data: rowData,
    metadata: { key: rowKey },
    selector: selectorRenderer,
    isSelected,
    index: rowIndex,
    isTabbable: isTabbableMode
  };

  return (
    <div
      aria-colindex={1}
      class={cellClasses}
      tabIndex={hasTabIndex ? 0 : -1}
      role={'gridcell'}
      data-oj-cell-type={'data'}
      data-oj-list-focusable={listId}
      data-oj-list-data={listId}
      {...(isSelectionEnabled
        ? isSelected
          ? { 'aria-selected': true }
          : { 'aria-selected': false }
        : {})}>
      {renderer(cellRendererProps)}
    </div>
  );
}
